import { useEffect, useState } from "react";
import { CalendarPlus, CheckCircle2, ExternalLink } from "lucide-react";
import { assetUrl } from "@/lib/api";
import { useFonts } from "@/components/LandingPage";

const LOGO_H = { sm: "h-7", md: "h-10", lg: "h-14" };
const stamp = (iso) => new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");

const calendarLinks = (c, ty) => {
  if (!ty.calendar_start || !ty.calendar_end) return null;
  const s = stamp(ty.calendar_start), e = stamp(ty.calendar_end);
  const page = typeof window !== "undefined" ? `${window.location.origin}/c/${c.slug}` : "";
  const details = [c.headline, page].filter(Boolean).join("\n\n");
  const google = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(c.title)}&dates=${s}/${e}`
    + `&details=${encodeURIComponent(details)}&location=${encodeURIComponent(ty.calendar_location || page)}`;
  const esc = (t) => (t || "").replace(/[,;\\]/g, (m) => `\\${m}`).replace(/\n/g, "\\n");
  const ics = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//StreamVault//EN", "BEGIN:VEVENT", `UID:${c.slug}-${s}@streamvault`,
    `DTSTAMP:${stamp(new Date().toISOString())}`, `DTSTART:${s}`, `DTEND:${e}`, `SUMMARY:${esc(c.title)}`,
    `DESCRIPTION:${esc(details)}`, `LOCATION:${esc(ty.calendar_location || page)}`, "END:VEVENT", "END:VCALENDAR"].join("\r\n");
  return { google, ics: `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}` };
};

export const ThankYouScreen = ({ campaign: c, onContinue, preview = false }) => {
  const b = c.branding;
  const ty = c.thank_you;
  const light = b.theme === "light";
  const cont = ty.mode !== "only";
  const [left, setLeft] = useState(ty.auto_seconds || 0);
  useFonts(b.font_heading, b.font_body);

  useEffect(() => {
    if (preview || !cont || !ty.auto_seconds) return undefined;
    if (left <= 0) { onContinue?.(); return undefined; }
    const t = setTimeout(() => setLeft((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [left, cont, preview, ty.auto_seconds]); // eslint-disable-line react-hooks/exhaustive-deps

  const cal = calendarLinks(c, ty);
  const radius = b.button_style === "sharp" ? "rounded-md" : "rounded-full";
  const bg = b.bg_type === "image" && b.bg_image_path
    ? { backgroundImage: `url(${assetUrl(b.bg_image_path)})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { backgroundColor: b.bg_color };
  const ghost = `inline-flex items-center gap-2 h-11 px-5 ${radius} border text-sm font-medium transition-colors ${light ? "border-slate-300 text-slate-700 hover:bg-slate-100" : "border-white/20 text-white hover:bg-white/10"}`;

  return (
    <div data-testid="thank-you-screen" style={{ ...bg, fontFamily: `"${b.font_body}", sans-serif` }}
      className={`${preview ? "min-h-[560px]" : "min-h-screen"} grid place-items-center px-6 py-16 ${light ? "text-slate-900" : "text-white"}`}>
      <div className={`w-full max-w-xl text-center rounded-2xl p-10 sm:p-12 sv-rise ${light ? "bg-white/90 shadow-xl" : "bg-black/40 border border-white/10 backdrop-blur-md"}`}>
        {b.logo_path && <img src={assetUrl(b.logo_path)} alt="" className={`${LOGO_H[b.logo_size] || LOGO_H.md} mx-auto mb-8 object-contain`} />}
        <CheckCircle2 className="w-14 h-14 mx-auto mb-6" style={{ color: b.accent }} />
        <h1 data-testid="thank-you-heading" className="text-4xl sm:text-5xl font-bold tracking-tight mb-4"
          style={{ fontFamily: `"${b.font_heading}", sans-serif`, ...(b.heading_color ? { color: b.heading_color } : {}) }}>{ty.heading}</h1>
        {ty.message && <p data-testid="thank-you-message" className={`text-base whitespace-pre-line mb-8 ${light ? "text-slate-600" : "text-slate-300"}`}>{ty.message}</p>}
        <div className="flex flex-wrap justify-center gap-3">
          {cont && (
            <button type="button" data-testid="thank-you-continue" onClick={() => !preview && onContinue?.()}
              className={`h-11 px-6 ${radius} text-sm font-semibold text-white hover:opacity-90 transition-opacity`} style={{ backgroundColor: b.accent }}>
              {ty.button_text || "Continue"}
            </button>
          )}
          {cal && <>
            <a data-testid="thank-you-calendar-google" href={cal.google} target="_blank" rel="noreferrer" className={ghost}><CalendarPlus className="w-4 h-4" /> Google Calendar</a>
            <a data-testid="thank-you-calendar-ics" href={cal.ics} download={`${c.slug}.ics`} className={ghost}><CalendarPlus className="w-4 h-4" /> Outlook / Apple (.ics)</a>
          </>}
          {ty.link_url && ty.link_text && (
            <a data-testid="thank-you-extra-link" href={ty.link_url} target="_blank" rel="noreferrer" className={ghost}>{ty.link_text} <ExternalLink className="w-3.5 h-3.5" /></a>
          )}
        </div>
        {cont && ty.auto_seconds > 0 && (
          <p data-testid="thank-you-countdown" className={`text-xs mt-6 ${light ? "text-slate-500" : "text-slate-400"}`}>
            Continuing automatically in {preview ? ty.auto_seconds : left}s…
          </p>
        )}
      </div>
    </div>
  );
};
